// src/components/category/CategoryEdit.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import Dropzone from "react-dropzone";
import axios from "axios";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const CategoryEdit = () => {
  const { slug, id } = useParams();
  const navigate  = useNavigate();

  /* ---------- state ---------- */
  const [formData, setFormData] = useState({
    name: "", title: "", description: "", image: null, parent: "",
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [hasParent, setHasParent]       = useState(false);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  /* mevcut modüldeki tüm kategoriler  */
  const [allCategories,    setAllCategories]    = useState([]);
  const [parentCandidates, setParentCandidates] = useState([]);   // self+children filtrelenmiş

  /* ---------- initial fetch ---------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const auth  = { headers: { Authorization: `Bearer ${token}` } };

        /* 1) düzenlenecek kategori */
        const { data: catRes } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/category/id/${id}`, auth
        );
        const cat = catRes.data;
        console.log("Backend'den gelen parent:", cat.parent);

        setFormData({
          name: cat.name ?? "",
          title: cat.title ?? "",
          description: cat.description ?? "",
          image: null,
          parent: cat.parent !== 0 ? String(cat.parent) : "",  // sadece !== 0 kontrolü yeterli
        });
        
        setHasParent(Boolean(cat.parent && cat.parent !== 0)); 
        setPreviewImage(
          cat.picture?.startsWith("uploads/") ? `/${cat.picture}` : cat.picture
        );

        /* 2) modüldeki tüm kategoriler */
        const { data: list } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/category?module_id=${cat.module_id}`, auth
        );
        setAllCategories(list);          // tüm ağaç tutulur

        /* 3) self + children hariç listele */
        const getChildren = (arr, pid) => {
          const direct = arr.filter(c => c.parent === pid);
          return direct.flatMap(d => [d.id, ...getChildren(arr, d.id)]);
        };
        const childIds = getChildren(list, Number(id));
        const filtered = list.filter(
          c => c.id !== Number(id) && !childIds.includes(c.id)
        );
        setParentCandidates(filtered);
      } catch (err) {
        console.error("Veriler alınamadı:", err);
      }
    };
    fetchData();
  }, [id]);

  /* ---------- hiyerarşik label oluştur ---------- */
  const parentOpts  = useMemo(() => {
    const buildTree = (items, parent = 0, level = 0) =>
      items
        .filter(i => i.parent === parent)
        .flatMap(i => [
          { id: i.id, label: `${"\u2937 ".repeat(level)}${i.name}` },
          ...buildTree(items, i.id, level + 1),
        ]);
    return buildTree(parentCandidates);
  }, [parentCandidates]);

  /* ---------- handlers ---------- */
  const onInput  = e => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  const onDrop   = files => {
    if (!files.length) return;
    const file = files[0];
    setFormData(p => ({ ...p, image: file }));
    setPreviewImage(URL.createObjectURL(file));
  };

  const removeImg = () => {
    setPreviewImage(null);
    setFormData(p => ({ ...p, image: null }));
  };

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const fd = new FormData();
      fd.append("name",        formData.name);
      fd.append("title",       formData.title);
      fd.append("description", formData.description);
      fd.append("slug",        slug);
      fd.append("parent", hasParent ? formData.parent : "");
      if (formData.image) fd.append("image", formData.image);
      else if (previewImage === null) fd.append("imageRemoved", true);

      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/category/id/${id}`, fd, {
        headers: { Authorization: `Bearer ${token}`,
                   "Content-Type": "multipart/form-data" },
      });
      alert("Kategori başarıyla güncellendi!");
      navigate(`/modules/${slug}`);
    } catch (err) {
      console.error("Güncelleme hatası:", err);
      alert("Kategori güncellenemedi!");
    } finally { setLoading(false); }
  };

  /* ---------- render ---------- */
  return (
    <div className="container my-4">
    {/* --------- KART 1 : GENEL BİLGİLER --------- */}
    <div className="card p-4 shadow-sm mb-4">

      {/* Kategori Adı */}
      <Form.Group className="mb-3">
      <Form.Label>{getText("CATEGORY_NAME")}</Form.Label>
        <Form.Control
          name="name"
          value={formData.name}
          onChange={onInput}
        />
      </Form.Group>

      {/* Görsel (opsiyonel) */}
      <Form.Group className="mb-4">
        <Form.Label>{getText("CATEGORY_IMAGE_OPTIONAL")}</Form.Label>
        <Dropzone onDrop={onDrop} accept={{ "image/*": [] }} multiple={false}>
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className="border p-3 text-center"
              style={{ cursor: "pointer", borderStyle: "dashed" }}
            >
              <input {...getInputProps()} />
              {previewImage ? (
                <>
                  <img src={previewImage} alt="" width={100} className="mb-2" />
                  {formData.image && <div>{formData.image.name}</div>}
                  <Button variant="danger" size="sm" onClick={removeImg}>
                  {getText("REMOVE_IMAGE")}
                  </Button>
                </>
              ) : (
                <p>{getText("DROP_IMAGE")}</p>
              )}
            </div>
          )}
        </Dropzone>
      </Form.Group>

      {/* Üst kategori anahtarı */}
      <Form.Check
        className="mb-3"
        type="checkbox"
        label={getText("WANT_PARENT_CATEGORY")}
        checked={hasParent}
        onChange={(e) => {
          setHasParent(e.target.checked);
          if (!e.target.checked) setFormData((p) => ({ ...p, parent: "" }));
        }}
      />

      {/* Üst kategori seçimi */}
      {hasParent && parentOpts.length > 0 && (
        <Form.Group className="mb-2">
          <Form.Label>{getText("PARENT_CATEGORY")}</Form.Label>
          <Form.Select
            name="parent"
            value={formData.parent}
            onChange={onInput}
          >
            <option value="">{getText("SELECT_OPTION")}</option>
            {parentOpts.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}
    </div>

    {/* --------- KART 2 : META BİLGİLER --------- */}
    <div className="card p-4 shadow-sm">

      <Form onSubmit={submit}>
        <Form.Group className="mb-3">
          <Form.Label>{getText("TITLE")}</Form.Label>
          <Form.Control
            name="title"
            value={formData.title}
            onChange={onInput}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>{getText("DESCRIPTION")}</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            name="description"
            value={formData.description}
            onChange={onInput}
          />
        </Form.Group>

        <div className="text-center">
          <Button type="submit" disabled={loading}>
            {loading ?  getText("SAVING", "Kaydediliyor...") : getText("SAVE", "Kaydet")}
          </Button>
        </div>
      </Form>
    </div>
  </div>
);
};


export default CategoryEdit;
